package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.milta.core.localization.WebImportRouteStrings
import com.milta.feature.route.presentation.WebImportRouteIntent
import com.milta.feature.route.presentation.WebImportRouteRow
import com.milta.feature.route.presentation.WebImportRouteState
import com.milta.feature.team.domain.entity.Driver
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr

/**
 * Écran 2 (+2b/2c/2d) — file-card, driver assignment card, submit CTA (left column) and the stops
 * table with review/correction (right column), mockup `docs/mockups/MIL-50.html`'s `.verify-grid`.
 */
@Composable
internal fun WebVerifyAssignRouteScreen(
    state: WebImportRouteState,
    strings: WebImportRouteStrings,
    onIntent: (WebImportRouteIntent) -> Unit,
) {
    var editingRowId by remember { mutableStateOf<Int?>(null) }

    Div {
        // Écran 2d — bandeau "tournée déjà envoyée" (409), au-dessus de la grille.
        if (state.submitError == "already_dispatched") {
            Div(attrs = { style { property("margin-bottom", "14px") } }) {
                InlineBanner(kind = InlineBannerKind.Error, text = strings.alreadyDispatchedBanner)
            }
        }

        Div(
            attrs = {
                style {
                    property("display", "grid")
                    property("grid-template-columns", "270px 1fr")
                    property("gap", "18px")
                    property("align-items", "start")
                }
            },
        ) {
            Div {
                FileCard(state, strings, onIntent)
                MultiDriverAssignCard(
                    state = state,
                    strings = strings,
                    onToggleDriver = { driverId -> onIntent(WebImportRouteIntent.ToggleDriver(driverId)) },
                )
                SubmitButton(state, strings, onIntent)
            }

            TableCard(
                state = state,
                strings = strings,
                editingRowId = editingRowId,
                onToggleEdit = { rowId -> editingRowId = if (editingRowId == rowId) null else rowId },
                onIntent = onIntent,
            )
        }
    }
}

@Composable
private fun FileCard(state: WebImportRouteState, strings: WebImportRouteStrings, onIntent: (WebImportRouteIntent) -> Unit) {
    Div(
        attrs = {
            style {
                property("background", WebSurface)
                property("border", "1px solid $WebSuccessInk")
                property("border-radius", "12px")
                padding(14.px)
                property("margin-bottom", "14px")
            }
        },
    ) {
        Div(
            attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "8px"); property("font-size", "11px"); property("font-weight", "700"); property("color", WebSuccessInk); property("margin-bottom", "10px") } },
        ) {
            Div(
                attrs = { style { width(18.px); height(18.px); property("border-radius", "50%"); property("background", WebSuccessInk); property("color", "#FFFFFF"); property("display", "flex"); property("align-items", "center"); property("justify-content", "center"); property("font-size", "11px") } },
            ) { Text("✓") }
            Text(strings.fileImportedStatus)
        }
        Div(attrs = { style { property("display", "flex"); property("gap", "10px"); property("align-items", "center") } }) {
            Div(
                attrs = { style { width(36.px); height(36.px); property("border-radius", "8px"); property("background", WebSurfaceDim); property("display", "flex"); property("align-items", "center"); property("justify-content", "center"); property("flex-shrink", "0") } },
            ) { Text("⬛") }
            Div {
                Div(attrs = { style { property("font-size", "13.5px"); property("font-weight", "700"); property("color", WebTextPrimary) } }) { Text(state.fileName) }
                Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary); property("margin-top", "1px") } }) {
                    Text("${state.totalCount} ${strings.stopsDetectedSuffix}")
                }
            }
        }
        FilePickerLabel(accept = ".xlsx,.xls,.csv,.pdf", onFilePicked = { bytes, name -> onIntent(WebImportRouteIntent.PickFile(bytes, name)) }) {
            Div(attrs = { style { property("display", "inline-block"); property("margin-top", "10px"); property("font-size", "12.5px"); property("color", WebPrimaryMid); property("font-weight", "600") } }) {
                Text(strings.replaceFileLink)
            }
        }
    }
}

/** MIL-51 — replaces the former single-selection dropdown ([AssignCard]) with a checkbox list: any
 * number of the enterprise's drivers can now be checked, always submitted through `POST
 * /route-batches` (see [WebImportRouteViewModel.submit]). */
@Composable
private fun MultiDriverAssignCard(
    state: WebImportRouteState,
    strings: WebImportRouteStrings,
    onToggleDriver: (String) -> Unit,
) {
    val isInvalid = state.submitError == "driver_required"
    Div(
        attrs = {
            style {
                property("background", WebSurface)
                property("border", "1px solid " + (if (isInvalid) WebError else WebBorder))
                property("border-radius", "12px")
                padding(16.px)
                property("margin-bottom", "16px")
            }
        },
    ) {
        Div(
            attrs = { style { property("font-size", "10.5px"); property("font-weight", "700"); property("letter-spacing", "0.06em"); property("text-transform", "uppercase"); property("color", WebTextSecondary); property("margin-bottom", "10px") } },
        ) { Text(strings.assignToEyebrow) }

        when {
            state.isLoadingDrivers -> Div(attrs = { style { padding(10.px, 0.px); property("font-size", "13px"); property("color", WebTextSecondary) } }) { Text("…") }

            state.drivers.isEmpty() -> Div(attrs = { style { padding(10.px, 0.px); property("font-size", "13px"); property("color", WebTextSecondary) } }) { Text(strings.dashPlaceholder) }

            else -> {
                Div(
                    attrs = { style { property("border", "1px solid $WebBorder"); property("border-radius", "9px"); property("overflow", "hidden") } },
                ) {
                    state.drivers.forEachIndexed { index, driver ->
                        DriverCheckboxRow(
                            driver = driver,
                            selected = driver.id in state.selectedDriverIds,
                            showBottomBorder = index < state.drivers.lastIndex,
                            onClick = { onToggleDriver(driver.id) },
                        )
                    }
                }
                Div(
                    attrs = { style { property("font-size", "12px"); property("font-weight", "700"); property("color", WebPrimary); property("margin-top", "8px"); property("text-align", "right") } },
                ) { Text("${state.selectedDriverIds.size} ${strings.selectedDriversCountSuffix}") }
            }
        }

        if (isInvalid) {
            Div(attrs = { style { property("font-size", "12px"); property("color", WebErrorInk); property("margin-top", "6px") } }) {
                Text(strings.selectDriverRequiredError)
            }
        }
    }
}

@Composable
private fun DriverCheckboxRow(driver: Driver, selected: Boolean, showBottomBorder: Boolean, onClick: () -> Unit) {
    Div(
        attrs = {
            onClick { onClick() }
            style {
                property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between")
                padding(10.px, 14.px)
                property("font-size", "13.5px")
                property("background", WebSurface)
                property("cursor", "pointer")
                if (showBottomBorder) property("border-bottom", "1px solid $WebSurfaceDim")
            }
        },
    ) {
        Div(attrs = { style { property("display", "flex"); property("flex-direction", "column") } }) {
            Div(attrs = { style { property("font-weight", "600"); property("color", WebTextPrimary) } }) { Text("${driver.firstName} ${driver.lastName}") }
            Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary) } }) { Text(driver.phoneNumber) }
        }
        Div(
            attrs = {
                style {
                    width(20.px); height(20.px)
                    property("border-radius", "6px")
                    property("flex-shrink", "0")
                    property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                    property("background", if (selected) WebPrimary else WebSurfaceDim)
                    property("border", "1px solid " + (if (selected) WebPrimary else WebBorder))
                    property("color", "#FFFFFF"); property("font-size", "12px"); property("font-weight", "700")
                }
            },
        ) { if (selected) Text("✓") }
    }
}

@Composable
private fun SubmitButton(state: WebImportRouteState, strings: WebImportRouteStrings, onIntent: (WebImportRouteIntent) -> Unit) {
    val disabled = state.isSubmitting || state.submitError == "driver_required" || state.submitError == "already_dispatched"
    Button(
        attrs = {
            if (!disabled) onClick { onIntent(WebImportRouteIntent.Submit) }
            style {
                gradientButtonStyle(this)
                width(100.percent)
                if (disabled) {
                    property("opacity", "0.5")
                    property("cursor", "not-allowed")
                    property("box-shadow", "none")
                }
            }
        },
    ) {
        // Ad hoc feature (2026-08) — RoutaR kill switch: when known off ahead of time, this is the
        // only submit CTA shown at all (never "Optimiser l'itinéraire", which would fail every
        // time) — same single button, relabeled, dispatching with skipOptimization = true.
        Text(if (state.routarEnabled) strings.optimizeRouteButton else strings.sendWithoutOptimizationButton)
    }

    if (state.submitError != null && state.submitError != "driver_required" && state.submitError != "already_dispatched") {
        val message = when (state.submitError) {
            "route_not_found" -> strings.routeNotFoundError
            "validation_error" -> strings.validationErrorGeneric
            "optimization_unavailable" -> strings.submitOptimizationUnavailableError
            else -> strings.submitServerError
        }
        Div(attrs = { style { property("font-size", "12px"); property("color", WebErrorInk); property("margin-top", "8px") } }) { Text(message) }
    }

    // Écran 2d — RoutaR indisponible : offre le round-robin déjà utilisé côté MIL-39 (`optimize =
    // false`) plutôt que de bloquer le patron sur "réessayer plus tard".
    if (state.submitError == "optimization_unavailable") {
        Button(
            attrs = {
                if (!state.isSubmitting) onClick { onIntent(WebImportRouteIntent.SubmitWithoutOptimization) }
                style {
                    width(100.percent)
                    property("margin-top", "8px")
                    property("padding", "10px")
                    property("border-radius", "10px")
                    property("border", "1px solid $WebBorder")
                    property("background", WebSurface)
                    property("color", WebTextPrimary)
                    property("font-size", "13px"); property("font-weight", "600")
                    if (state.isSubmitting) {
                        property("opacity", "0.5")
                        property("cursor", "not-allowed")
                    } else {
                        property("cursor", "pointer")
                    }
                }
            },
        ) { Text(strings.sendWithoutOptimizationButton) }
    }
}

@Composable
private fun TableCard(
    state: WebImportRouteState,
    strings: WebImportRouteStrings,
    editingRowId: Int?,
    onToggleEdit: (Int) -> Unit,
    onIntent: (WebImportRouteIntent) -> Unit,
) {
    Div(
        attrs = { style { property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "12px"); property("overflow", "hidden") } },
    ) {
        Div(
            attrs = { style { property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center"); padding(14.px, 18.px); property("border-bottom", "1px solid $WebBorder") } },
        ) {
            Div(attrs = { style { property("font-size", "13.5px"); property("font-weight", "600"); property("color", WebTextPrimary) } }) { Text(strings.tableCaptionLead) }
            Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary) } }) { Text(strings.tableCaptionHint) }
        }

        if (!state.isStructuredFormat || state.incompleteCount > 0) {
            Div(attrs = { style { padding(14.px, 18.px, 0.px, 18.px) } }) {
                if (!state.isStructuredFormat) {
                    InlineBanner(kind = InlineBannerKind.Info, text = strings.freeFormatBanner)
                    Div(attrs = { style { height(8.px) } })
                }
                if (state.incompleteCount > 0) {
                    InlineBanner(kind = InlineBannerKind.Warning, text = "${state.incompleteCount} ${strings.incompleteStopsBannerSuffix}")
                }
            }
        }

        Table(attrs = { style { width(100.percent); property("border-collapse", "collapse"); property("font-size", "13.5px") } }) {
            Thead {
                Tr {
                    HeaderCell(strings.columnHeaderIndex)
                    HeaderCell(strings.columnHeaderRecipientName)
                    HeaderCell(strings.columnHeaderAddress)
                    HeaderCell(strings.columnHeaderPhone)
                    HeaderCell(strings.columnHeaderDeadline)
                    HeaderCell("")
                }
            }
            Tbody {
                state.rows.forEachIndexed { index, row ->
                    RowView(
                        index = index + 1,
                        row = row,
                        strings = strings,
                        isEditing = editingRowId == row.id,
                        onToggleEdit = { onToggleEdit(row.id) },
                        onRowChanged = { updated -> onIntent(WebImportRouteIntent.UpdateRow(row.id, updated)) },
                    )
                }
            }
        }
    }
}

@Composable
private fun HeaderCell(label: String) {
    Th(
        attrs = { style { property("text-align", "left"); property("font-size", "10.5px"); property("font-weight", "700"); property("letter-spacing", "0.05em"); property("text-transform", "uppercase"); property("color", WebTextSecondary); padding(10.px, 16.px); property("background", WebSurfaceDim); property("border-bottom", "1px solid $WebBorder") } },
    ) { Text(label) }
}

@Composable
private fun RowView(
    index: Int,
    row: WebImportRouteRow,
    strings: WebImportRouteStrings,
    isEditing: Boolean,
    onToggleEdit: () -> Unit,
    onRowChanged: (WebImportRouteRow) -> Unit,
) {
    Tr(attrs = { if (row.isIncomplete) style { property("background", WebWarningBg) } }) {
        Td(attrs = { style { padding(12.px, 16.px); property("color", WebTextSecondary); property("border-bottom", "1px solid $WebSurfaceDim") } }) { Text(index.toString()) }

        if (isEditing) {
            EditableCell(row.recipientName.orEmpty()) { onRowChanged(row.copy(recipientName = it)) }
            EditableCell(row.address.orEmpty()) { onRowChanged(row.copy(address = it)) }
            EditableCell(row.phoneNumber.orEmpty()) { onRowChanged(row.copy(phoneNumber = it.ifBlank { null })) }
            EditableCell(row.deadlineTime.orEmpty()) { onRowChanged(row.copy(deadlineTime = it.ifBlank { null })) }
        } else {
            Td(attrs = { style { padding(12.px, 16.px); property("font-weight", "600"); property("color", WebTextPrimary); property("border-bottom", "1px solid $WebSurfaceDim") } }) {
                Text(row.recipientName?.takeIf { it.isNotBlank() } ?: strings.dashPlaceholder)
                if (row.recipientName.isNullOrBlank()) {
                    Div(attrs = { style { property("display", "inline-flex"); property("align-items", "center"); property("gap", "5px"); property("font-size", "11px"); property("font-weight", "700"); property("color", WebWarning); property("margin-top", "2px") } }) {
                        Text(strings.rowFlagMissingName)
                    }
                }
            }
            Td(attrs = { style { padding(12.px, 16.px); property("color", if (row.address.isNullOrBlank()) WebTextSecondary else WebTextPrimary); property("border-bottom", "1px solid $WebSurfaceDim") } }) {
                Text(row.address?.takeIf { it.isNotBlank() } ?: strings.dashPlaceholder)
                if (row.address.isNullOrBlank()) {
                    Div(attrs = { style { property("display", "inline-flex"); property("align-items", "center"); property("gap", "5px"); property("font-size", "11px"); property("font-weight", "700"); property("color", WebWarning); property("margin-top", "2px") } }) {
                        Text(strings.rowFlagMissingAddress)
                    }
                }
            }
            Td(attrs = { style { padding(12.px, 16.px); property("color", WebTextPrimary); property("font-variant-numeric", "tabular-nums"); property("border-bottom", "1px solid $WebSurfaceDim") } }) {
                Text(row.phoneNumber?.takeIf { it.isNotBlank() } ?: strings.dashPlaceholder)
            }
            Td(attrs = { style { padding(12.px, 16.px); property("color", WebTextPrimary); property("font-variant-numeric", "tabular-nums"); property("border-bottom", "1px solid $WebSurfaceDim") } }) {
                Text(row.deadlineTime?.takeIf { it.isNotBlank() } ?: strings.dashPlaceholder)
            }
        }

        Td(attrs = { style { padding(12.px, 16.px); property("border-bottom", "1px solid $WebSurfaceDim") } }) {
            Button(
                attrs = {
                    onClick { onToggleEdit() }
                    style {
                        width(28.px); height(28.px); property("border-radius", "8px")
                        property("border", "1px solid $WebBorder"); property("background", WebSurface)
                        property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                        property("cursor", "pointer"); property("float", "right")
                    }
                },
            ) { Text(if (isEditing) "✓" else "✎") }
        }
    }
}

@Composable
private fun EditableCell(value: String, onChange: (String) -> Unit) {
    Td(attrs = { style { padding(8.px, 16.px); property("border-bottom", "1px solid $WebSurfaceDim") } }) {
        Input(
            type = InputType.Text,
            attrs = {
                value(value)
                onInput { event -> onChange(event.value) }
                style {
                    width(100.percent); property("box-sizing", "border-box")
                    property("border", "1px solid $WebBorder"); property("border-radius", "6px")
                    property("padding", "6px 8px"); property("font-size", "13px")
                }
            },
        )
    }
}

internal enum class InlineBannerKind { Info, Warning, Error, Success }

@Composable
internal fun InlineBanner(kind: InlineBannerKind, text: String) {
    val (bg, ink) = when (kind) {
        InlineBannerKind.Info -> WebActiveTint to WebPrimary
        InlineBannerKind.Warning -> WebWarningBg to WebWarning
        InlineBannerKind.Error -> WebErrorBg to WebErrorInk
        InlineBannerKind.Success -> WebSuccessBg to WebSuccessInk
    }
    Div(
        attrs = {
            style {
                property("display", "flex"); property("align-items", "center"); property("gap", "10px")
                property("border-radius", "10px"); padding(11.px, 14.px)
                property("font-size", "13px"); property("margin-bottom", "14px")
                property("background", bg); property("color", ink)
            }
        },
    ) { Text(text) }
}
